import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiLoader, FiDollarSign, FiLink } = FiIcons;

const CATEGORIES = ['apparel', 'vinyl', 'digital', 'accessories', 'bundle', 'other'];
const inputCls = 'w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white font-bold placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors';

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{label}</label>
    {children}
  </div>
);

const MerchModal = ({ item, onClose, onSave }) => {
  const isEdit = !!item?.id;
  const [form, setForm] = useState({ title: '', description: '', price: '', image_url: '', buy_url: '', category: 'apparel', is_active: true });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (item) setForm({ title: item.title || '', description: item.description || '', price: String(item.price || ''), image_url: item.image_url || '', buy_url: item.buy_url || '', category: item.category || 'apparel', is_active: item.is_active !== false });
  }, [item]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setErr('Title is required.'); return; }
    setLoading(true); setErr('');
    const { error } = await onSave(isEdit ? item.id : null, { ...form, price: parseFloat(form.price) || 0 });
    setLoading(false);
    if (error) setErr(error.message);
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-7 py-6 border-b border-zinc-900">
          <h3 className="text-xl font-black text-white uppercase tracking-tight">{isEdit ? 'Edit' : 'Add'} Merch</h3>
          <button onClick={onClose} className="p-2 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white transition-all">
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>
        <div className="p-7 space-y-4 max-h-[65vh] overflow-y-auto">
          <Field label="Title *">
            <input className={inputCls} placeholder="e.g. Tour Tee — Black" value={form.title} onChange={e => set('title', e.target.value)} />
          </Field>
          <Field label="Description">
            <textarea className={`${inputCls} resize-none`} rows={2} placeholder="Short description..." value={form.description} onChange={e => set('description', e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price (USD)">
              <div className="relative">
                <SafeIcon icon={FiDollarSign} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                <input type="number" min="0" step="0.01" className={`${inputCls} pl-9`} placeholder="0.00" value={form.price} onChange={e => set('price', e.target.value)} />
              </div>
            </Field>
            <Field label="Category">
              <select className={inputCls} value={form.category} onChange={e => set('category', e.target.value)}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Product Image URL">
            <input className={inputCls} placeholder="https://..." value={form.image_url} onChange={e => set('image_url', e.target.value)} />
          </Field>
          {form.image_url && (
            <div className="w-24 h-24 rounded-xl overflow-hidden border border-zinc-800">
              <img src={form.image_url} alt="Merch" className="w-full h-full object-cover" />
            </div>
          )}
          <Field label="Buy Link">
            <div className="relative">
              <SafeIcon icon={FiLink} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
              <input className={`${inputCls} pl-9`} placeholder="https://your-store.com/..." value={form.buy_url} onChange={e => set('buy_url', e.target.value)} />
            </div>
          </Field>
          <div className="flex items-center gap-3 p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <button onClick={() => set('is_active', !form.is_active)}
              className={`w-12 h-6 rounded-full transition-all flex items-center px-1 ${form.is_active ? 'bg-green-500 justify-end' : 'bg-zinc-700 justify-start'}`}>
              <div className="w-4 h-4 rounded-full bg-white shadow" />
            </button>
            <p className="text-xs font-black uppercase tracking-widest text-white">{form.is_active ? 'Active' : 'Hidden'}</p>
          </div>
          {err && <p className="text-red-400 text-xs font-black">{err}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 px-7 py-5 border-t border-zinc-900">
          <button onClick={onClose} className="px-5 py-3 border border-zinc-800 rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:border-zinc-600 hover:text-white transition-all">Cancel</button>
          <button onClick={handleSave} disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-white text-black text-xs font-black uppercase tracking-widest rounded-2xl hover:scale-105 transition-all disabled:opacity-50">
            {loading && <SafeIcon icon={FiLoader} className="animate-spin text-sm" />}
            {isEdit ? 'Save Changes' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MerchModal;